import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { useProductDetailContext } from "@contexts/product-detail-context";

import MedleyDetails from "@components/store/product-detail/medley/medley-details";
import ProductCarousel from "@components/store/product-detail/shared/product-detail-carousel";
import ProductDetailHeader from "@components/store/product-detail/shared/product-detail-header";

import { useProductInfoContext } from "@contexts/product-info-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import {
  getIsPricelessShop,
  selectStoreItemsPriceRange,
  getStoreMedleyPriceOptions,
  getStoreStcMedleyPriceOptions,
  getStoreUsdMedleyPriceOptions,
} from "@selectors/store";

import { US_CURRENCY_CODE } from "@constants/store";
import { DEFAULT_QUANTITY } from "@constants/products/detail";

import styles from "./main.module.scss";

const Medley = ({ product, masterVariant, canBuyProduct }) => {
  const { modalClasses, hidePriceDetail } = useProductInfoContext();

  const { productImgClass = "", productContentClass = "" } = modalClasses;

  const [productQuantity, setProductQuantity] = useState(DEFAULT_QUANTITY);
  const [variantSelected, setSelectedVariant] = useState({});
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const {
    editProductIds: [editProductId],
  } = useProductDetailContext();

  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const usdMedleyPrices = useSelector(getStoreUsdMedleyPriceOptions);
  const stcMedleyPrices = useSelector(getStoreStcMedleyPriceOptions);
  const medleyPrices = useSelector(getStoreMedleyPriceOptions);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const { maxPrice } = useSelector(selectStoreItemsPriceRange);

  const { previewMode, previewModeCurrency } = usePreviewModeContext();

  const medleyPricesForPricelessShop = useMemo(() => {
    const pricessToBeSelectedFrom = [...usdMedleyPrices]?.reverse();
    const selectedMedleyPrice =
      pricessToBeSelectedFrom?.find(({ amount }) => amount <= maxPrice) || {};
    return [selectedMedleyPrice];
  }, [maxPrice, usdMedleyPrices]);

  const medleyProductOptions = useMemo(() => {
    let options = stcMedleyPrices;
    if (!previewMode) options = isPricelessShop ? medleyPricesForPricelessShop : medleyPrices;
    else if (previewModeCurrency.currencyCode === US_CURRENCY_CODE) options = usdMedleyPrices;
    return options.map((priceItem) => ({
      ...priceItem,
      amountIn: formatPriceInDisplayStoreCurrency(priceItem.amount),
    }));
  }, [
    previewMode,
    isPricelessShop,
    medleyPricesForPricelessShop,
    medleyPrices,
    previewModeCurrency,
    usdMedleyPrices,
    stcMedleyPrices,
    formatPriceInDisplayStoreCurrency,
  ]);

  const sliderImages = useMemo(
    () =>
      product.variantImages.map((image, index) => {
        return { id: `image${index}`, image };
      }),
    [product],
  );

  //reset product quantity on variant change
  useEffect(() => {
    !editProductId && setProductQuantity(DEFAULT_QUANTITY);
  }, [editProductId, variantSelected]);

  const getDisplayPrice = () => {
    const minPrice = medleyProductOptions[0].amountIn;
    const maxPrice = medleyProductOptions[medleyProductOptions.length - 1].amountIn;
    return `${minPrice} - ${maxPrice}`;
  };

  const renderProductDetailHeader = () => (
    <ProductDetailHeader
      displayPrice={!hidePriceDetail && getDisplayPrice()}
      showProductName
      product={product}
    />
  );

  const renderProductDetails = () => (
    <MedleyDetails
      product={product}
      masterVariant={masterVariant}
      productQuantity={productQuantity}
      setProductQuantity={setProductQuantity}
      variantSelected={variantSelected}
      setSelectedVariant={setSelectedVariant}
      canBuyProduct={canBuyProduct}
      renderProductDetailHeader={renderProductDetailHeader}
      medleyProductOptions={medleyProductOptions}
    />
  );

  return (
    <div>
      <div className={`${styles.rowProductDetail} ${styles.rowWithV2Enabled}`}>
        {isMobileDevice && renderProductDetailHeader()}
        <div className={`${styles.productCarousalWrapper} ${productImgClass}`}>
          <ProductCarousel
            sliderImages={sliderImages}
            imageMagnify
            productId={product.id}
            product={product}
          />
        </div>
        <div className={`${styles.prdCol} ${productContentClass}`}>{renderProductDetails()}</div>
      </div>
    </div>
  );
};

export default Medley;
